import { getFileSize } from 'app/utils/attachments';

import { ValidationWarning } from '../components/stateBar/stateBar.types';

const megaByte = 1024 * 1024; // 1MB
const fiveMB = 5 * megaByte; // 5MB
const sixteenMB = 16 * megaByte; // 16MB

/**
 * WhatsApp file type configurations with size limits per Twilio specifications.
 * https://www.twilio.com/docs/whatsapp/guidance-whatsapp-media-messages
 */
const WHATSAPP_FILE_TYPE_CONFIG: Record<string, number> = {
  // Image files (5MB limit)
  jpg: fiveMB,
  jpeg: fiveMB,
  png: fiveMB,
  // Document files (16MB limit)
  pdf: sixteenMB,
  doc: sixteenMB,
  docx: sixteenMB,
  pptx: sixteenMB,
  xlsx: sixteenMB,
  // Audio files (16MB limit)
  mp3: sixteenMB,
  amr: sixteenMB,
  aac: sixteenMB,
  // Video files (16MB limit)
  mp4: sixteenMB,
  // Contact files (16MB limit)
  vcf: sixteenMB,
};

type ValidateWhatsAppAttachmentOptions = {
  file: File;
  existingAttachmentsCount: number;
};

/**
 * Validates a file attachment for WhatsApp channel constraints. Returns null if
 * valid, or an error object with message and maxAllowedSize if validation
 * failed.
 */
export const validateWhatsAppAttachment = ({
  file,
  existingAttachmentsCount,
}: ValidateWhatsAppAttachmentOptions): ValidationWarning | null => {
  // Check if already has an attachment (WhatsApp allows only 1)
  if (existingAttachmentsCount >= 1) {
    return {
      type: 'file-count',
      arg: {
        count: existingAttachmentsCount,
        maxCount: 1,
      },
    };
  }

  // Check file type
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const maxSizeForType = fileExtension
    ? WHATSAPP_FILE_TYPE_CONFIG[fileExtension]
    : undefined;

  if (!fileExtension || !maxSizeForType) {
    return {
      type: 'file-type',
      arg: {
        fileType: fileExtension ?? 'unknown file type',
        supportedFileTypes: Object.keys(WHATSAPP_FILE_TYPE_CONFIG),
      },
    };
  }

  // Check file size against the limit for this file type
  if (file.size > maxSizeForType) {
    return {
      type: 'file-size',
      arg: {
        size: getFileSize(file.size),
        maxSize: getFileSize(maxSizeForType),
      },
    };
  }

  return null;
};
